import { PRODUCTS_TABLE, SUPABASE_ANON_KEY, SUPABASE_URL } from '../../config/supabase';
import { isAdminUser, normalize, onReady } from '../../utils/dom';

type ProductLike = {
  id?: string | number;
  name?: string;
  price?: unknown;
  category?: unknown;
  company?: unknown;
  featured?: boolean;
};

const state = {
  featuredByName: new Map<string, boolean>(),
  featuredById: new Map<string, boolean>(),
  observer: null as MutationObserver | null,
  injectTimer: 0,
  reorderTimer: 0,
  listCacheTimer: 0,
  controlsBound: false,
  networkPatchRegistered: false
};

const nativeFetch = window.fetch.bind(window);
const nativeSort = Array.prototype.sort;

function apiHeaders(preferRepresentation = false): HeadersInit {
  const headers: Record<string, string> = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json'
  };

  if (preferRepresentation) headers.Prefer = 'return=representation';
  return headers;
}

function looksLikeProduct(value: unknown): value is ProductLike {
  const obj = value as ProductLike;
  return Boolean(
    obj &&
      typeof obj === 'object' &&
      'name' in obj &&
      'price' in obj &&
      'category' in obj &&
      'company' in obj
  );
}

function isProductsRequest(url: string): boolean {
  return typeof url === 'string' && url.includes(`/rest/v1/${PRODUCTS_TABLE}`);
}

function addFeaturedToSelect(urlString: string): string {
  try {
    const url = new URL(urlString, window.location.origin);

    if (!isProductsRequest(url.toString())) return urlString;

    const select = url.searchParams.get('select');
    if (!select) return url.toString();

    const compact = select.replace(/\s+/g, '');
    if (compact === '*' || compact.includes('featured')) return url.toString();

    url.searchParams.set('select', `${select},featured`);
    return url.toString();
  } catch {
    return urlString;
  }
}

async function fetchFeaturedList(): Promise<ProductLike[]> {
  const url = `${SUPABASE_URL}/rest/v1/${PRODUCTS_TABLE}?select=id,name,featured&order=name.asc`;

  const response = await nativeFetch(url, {
    method: 'GET',
    headers: apiHeaders()
  });

  if (!response.ok) throw new Error('Errore caricamento featured list');

  const data = (await response.json()) as ProductLike[];
  state.featuredByName.clear();
  state.featuredById.clear();

  for (const product of data || []) {
    if (!product.name) continue;
    state.featuredByName.set(normalize(product.name), Boolean(product.featured));
    if (product.id != null) state.featuredById.set(String(product.id), Boolean(product.featured));
  }

  return data;
}

async function updateFeatured(productId: string, featured: boolean): Promise<void> {
  const url = `${SUPABASE_URL}/rest/v1/${PRODUCTS_TABLE}?id=eq.${encodeURIComponent(productId)}`;

  const response = await nativeFetch(url, {
    method: 'PATCH',
    headers: apiHeaders(true),
    body: JSON.stringify({ featured: Boolean(featured) })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Errore update featured: ${text}`);
  }

  state.featuredById.set(String(productId), Boolean(featured));
}

function getProductCards(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>('div.bg-\\[hsl\\(220\\,18\\%\\,10\\%\\)\\].border-\\[hsl\\(220\\,16\\%\\,18\\%\\)\\]')
  );
}

function extractNameFromCard(card: HTMLElement): string {
  const nameElement = card.querySelector<HTMLElement>('.text-lg.text-white');
  return normalize(nameElement?.textContent || '');
}

function extractImageWrap(card: HTMLElement): HTMLElement | null {
  return card.querySelector<HTMLElement>('.relative.w-full.h-40');
}

function extractProductIdFromCard(card: HTMLElement): string | null {
  const buttons = Array.from(card.querySelectorAll<HTMLButtonElement>('button'));

  for (const button of buttons) {
    const title = (button.getAttribute('title') || '').toLowerCase();
    if (!title.includes('carica immagine')) continue;

    const reactKeys = Object.keys(button).filter((key) => key.startsWith('__reactProps'));

    for (const key of reactKeys) {
      try {
        const props = (button as unknown as Record<string, unknown>)[key];
        const found = walkReactPropsForProductId(props, 0);
        if (found != null) return String(found);
      } catch {
        // React internals are intentionally best-effort here.
      }
    }
  }

  return null;
}

function walkReactPropsForProductId(value: unknown, depth: number): string | number | null {
  if (!value || depth > 6 || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  const id = record.id;

  if (
    (typeof id === 'string' || typeof id === 'number') &&
    ('name' in record || 'price' in record || 'company' in record)
  ) {
    return id;
  }

  for (const key in record) {
    const found = walkReactPropsForProductId(record[key], depth + 1);
    if (found != null) return found;
  }

  return null;
}

function ensureBadge(card: HTMLElement, featured: boolean): void {
  const nameElement = card.querySelector<HTMLElement>('.text-lg.text-white');
  if (!nameElement) return;

  let badge = card.querySelector<HTMLElement>('.featured-badge-inline');

  if (featured) {
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'featured-badge-inline';
      badge.textContent = '★ Preferito';
      nameElement.insertAdjacentElement('afterend', badge);
    }
    return;
  }

  badge?.remove();
}

function ensureStarOnCard(card: HTMLElement): void {
  if (!isAdminUser()) return;

  const imageWrap = extractImageWrap(card);
  if (!imageWrap) return;

  const name = extractNameFromCard(card);
  if (!name) return;

  const productId = extractProductIdFromCard(card);
  const featured = productId
    ? Boolean(state.featuredById.get(String(productId)))
    : Boolean(state.featuredByName.get(name));

  let star = imageWrap.querySelector<HTMLButtonElement>('.featured-star-btn');

  if (!star) {
    star = document.createElement('button');
    star.type = 'button';
    star.className = 'featured-star-btn';
    imageWrap.appendChild(star);
  }

  star.classList.toggle('is-featured', featured);
  star.textContent = featured ? '★' : '☆';
  star.title = featured ? 'Rimuovi dai preferiti' : 'Metti nei preferiti';
  star.dataset.productName = name;
  if (productId) star.dataset.productId = String(productId);

  ensureBadge(card, featured);

  if (star.dataset.bound === '1') return;
  star.dataset.bound = '1';

  star.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentName = star?.dataset.productName || '';
    const currentId = star?.dataset.productId || null;
    const currentValue = currentId
      ? Boolean(state.featuredById.get(currentId))
      : Boolean(state.featuredByName.get(currentName));

    if (!star) return;
    star.disabled = true;

    try {
      let targetId = currentId;

      if (!targetId) {
        const list = await fetchFeaturedList();
        const hit = list.find((product) => normalize(product.name) === currentName);
        if (!hit?.id) throw new Error('Prodotto non trovato');
        targetId = String(hit.id);
      }

      await updateFeatured(targetId, !currentValue);
      state.featuredByName.set(currentName, !currentValue);
      state.featuredById.set(String(targetId), !currentValue);

      star.dataset.productId = String(targetId);
      star.classList.toggle('is-featured', !currentValue);
      star.textContent = !currentValue ? '★' : '☆';
      star.title = !currentValue ? 'Rimuovi dai preferiti' : 'Metti nei preferiti';

      ensureBadge(card, !currentValue);

      window.setTimeout(() => {
        location.reload();
      }, 150);
    } catch (error) {
      console.error(error);
      alert('Non sono riuscito ad aggiornare il preferito.');
    } finally {
      star.disabled = false;
    }
  });
}

function injectStars(): void {
  if (!isAdminUser()) return;
  for (const card of getProductCards()) ensureStarOnCard(card);
}

function scheduleInject(): void {
  window.clearTimeout(state.injectTimer);
  state.injectTimer = window.setTimeout(injectStars, 250);
}

function startAdminStarsObserver(): void {
  if (state.observer) return;

  state.observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length > 0)) scheduleInject();
  });

  state.observer.observe(document.body, { childList: true, subtree: true });
}

function isCatalogPage(): boolean {
  return location.pathname.includes('/catalog');
}

function getCatalogCards(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('div.rounded-xl.border')).filter((card) => {
    const text = normalize(card.textContent);
    return text.includes('im distribuzione') || text.includes('prezzo su richiesta') || text.includes('codice:');
  });
}

function getCardTitle(card: HTMLElement): string {
  const candidates = Array.from(card.querySelectorAll<HTMLElement>('h1,h2,h3,h4,p,div,span'));

  for (const element of candidates) {
    const text = (element.textContent || '').trim();
    const lowerText = text.toLowerCase();

    if (!text || text.length < 8) continue;
    if (lowerText.includes('prezzo su richiesta')) continue;
    if (lowerText.includes('codice:')) continue;
    if (lowerText.includes('im distribuzione')) continue;

    return text;
  }

  return '';
}

function getGridParent(cards: HTMLElement[]): HTMLElement | null {
  const counts = new Map<HTMLElement, number>();

  for (const card of cards) {
    const parent = card.parentElement;
    if (!parent) continue;
    counts.set(parent, (counts.get(parent) || 0) + 1);
  }

  let bestParent: HTMLElement | null = null;
  let bestCount = 0;

  for (const [parent, count] of counts.entries()) {
    if (count > bestCount) {
      bestParent = parent;
      bestCount = count;
    }
  }

  return bestParent;
}

function addCatalogBadge(card: HTMLElement): void {
  if (card.querySelector('.featured-catalog-badge')) return;

  const titleElement = Array.from(card.querySelectorAll<HTMLElement>('h1,h2,h3,h4,p,div,span')).find((element) => {
    const text = (element.textContent || '').trim().toLowerCase();
    return text && !text.includes('prezzo su richiesta') && !text.includes('codice:') && text.length > 8;
  });

  if (!titleElement) return;

  const badge = document.createElement('div');
  badge.className = 'featured-catalog-badge';
  badge.textContent = '★ Preferito';
  titleElement.insertAdjacentElement('afterend', badge);
}

function removeCatalogBadge(card: HTMLElement): void {
  card.querySelector('.featured-catalog-badge')?.remove();
}

function reorderCatalogDom(): void {
  if (!isCatalogPage()) return;

  const cards = getCatalogCards();
  if (!cards.length) return;

  const parent = getGridParent(cards);
  if (!parent) return;

  const cardData = cards.map((card) => {
    const title = getCardTitle(card);
    const featured = Boolean(state.featuredByName.get(normalize(title)));

    if (featured) addCatalogBadge(card);
    else removeCatalogBadge(card);

    return { card, title, featured };
  });

  const featuredCards = cardData.filter((item) => item.featured);
  const normalCards = cardData.filter((item) => !item.featured);

  if (!featuredCards.length) return;

  for (const item of [...featuredCards, ...normalCards]) parent.appendChild(item.card);
}

function scheduleReorder(delay = 250): void {
  window.clearTimeout(state.reorderTimer);
  state.reorderTimer = window.setTimeout(reorderCatalogDom, delay);
}

function bindCatalogControls(): void {
  if (state.controlsBound) return;
  state.controlsBound = true;

  document.addEventListener(
    'input',
    (event) => {
      const element = event.target;
      if (!(element instanceof HTMLInputElement)) return;
      if (element.placeholder?.toLowerCase().includes('cerca')) scheduleReorder(350);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      if (event.target instanceof HTMLSelectElement) scheduleReorder(350);
    },
    true
  );

  document.addEventListener('click', () => scheduleReorder(500), true);
}

export function registerFeaturedNetworkPatch(): void {
  if (state.networkPatchRegistered) return;
  state.networkPatchRegistered = true;

  window.fetch = function patchedFeaturedFetch(input: RequestInfo | URL, init?: RequestInit) {
    try {
      if (typeof input === 'string') {
        input = addFeaturedToSelect(input);
      } else if (input instanceof Request) {
        const patchedUrl = addFeaturedToSelect(input.url);
        if (patchedUrl !== input.url) input = new Request(patchedUrl, input);
      }
    } catch (error) {
      console.warn('featured fetch patch warning', error);
    }

    return nativeFetch(input, init);
  };

  Array.prototype.sort = function patchedFeaturedSort<T>(this: T[], compareFn?: (a: T, b: T) => number): T[] {
    if (
      Array.isArray(this) &&
      this.length > 0 &&
      typeof compareFn === 'function' &&
      this.every((item) => looksLikeProduct(item))
    ) {
      return nativeSort.call(this, (a: T, b: T) => {
        const productA = a as ProductLike;
        const productB = b as ProductLike;
        const af = productA?.featured ? 1 : 0;
        const bf = productB?.featured ? 1 : 0;

        if (af !== bf) return bf - af;
        return compareFn(a, b);
      });
    }

    return nativeSort.call(this, compareFn as never);
  };
}

export function initFeaturedCatalogPatches(): void {
  onReady(async () => {
    try {
      await fetchFeaturedList();
    } catch (error) {
      console.warn('featured list preload failed', error);
    }

    scheduleInject();
    startAdminStarsObserver();

    window.addEventListener('storage', scheduleInject);
    window.clearInterval(state.listCacheTimer);
    state.listCacheTimer = window.setInterval(() => {
      if (isAdminUser()) fetchFeaturedList().then(scheduleInject).catch(() => undefined);
    }, 15000);

    if (isCatalogPage()) {
      bindCatalogControls();
      scheduleReorder(700);
      window.setTimeout(() => scheduleReorder(1200), 1200);
      window.setTimeout(() => scheduleReorder(2000), 2000);

      window.setInterval(async () => {
        if (!isCatalogPage()) return;
        try {
          await fetchFeaturedList();
          scheduleReorder(250);
        } catch {
          // Non bloccare il catalogo per errori temporanei di rete.
        }
      }, 15000);
    }
  });
}
